import { createSlice } from "@reduxjs/toolkit";
import { parseUrlParams } from "shared/helpers/urlHelpers";
import { PanoramaState } from "./panoramaStateModels";

const initialState: PanoramaState = {
  guid: parseUrlParams<PanoramaState["guid"]>("guid", "DemoPano"),
  currentField: parseUrlParams<PanoramaState["currentField"]>(
    "currentfield",
    null
  ),
  refreshCount: 0,
  currentFieldDetails: null,
  farmDetails: null,
  imperialUnits: false,
};

const farmDetailsSlice = createSlice({
  name: "farmDetails",
  initialState,
  reducers: {
    /**
     * Set pano GUID
     *
     */
    setGuid(state, action: { payload: NonNullable<PanoramaState["guid"]> }) {
      state.guid = action.payload;
    },
    /**
     * Set farm details
     */
    setFarmDetails(
      state,
      action: { payload: NonNullable<PanoramaState["farmDetails"]> }
    ) {
      state.farmDetails = action.payload;
    },
    /**
     * Set field details
     */
    setCurrentFieldDetails(
      state,
      action: { payload: NonNullable<PanoramaState["currentFieldDetails"]> }
    ) {
      // Update in Redux
      state.currentFieldDetails = action.payload;
    },
    /**
     * This is used to set the current field.
     * In all instances (except at the start), this will only be triggered by
     * an event _inside_ `skin_loadscene`
     */
    setCurrentField(
      state,
      action: {
        payload: {
          fieldId: NonNullable<PanoramaState["currentField"]>;
          forceReload?: boolean;
        };
      }
    ) {
      // Update in URL - this is for easier sharing
      // Idea: user `pushState` to allow navigation
      // NOTE: Will require some back/forward navigation listening
      // https://developer.mozilla.org/en-US/docs/Web/API/History/replaceState
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("currentfield", action.payload.fieldId);
      window.history.replaceState(null, "", newUrl.href);
      // Update in Redux
      state.currentField = action.payload.fieldId;
      if (action.payload.forceReload) {
        state.refreshCount += 1;
      }
      // Set in krpano
      // NOTE: This is used to keep `currentfield` up to date in krpano
      // It is initially set onload with `passQueryParameters`
      window.krpano.global.currentfield = action.payload.fieldId;
    },
  },
});

export const {
  setGuid,
  setFarmDetails,
  setCurrentFieldDetails,
  setCurrentField,
} = farmDetailsSlice.actions;

export default farmDetailsSlice.reducer;
